import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from 'react';
import { cartService } from '../services/cartService';
import { useAuth } from './AuthContext';

const CartContext = createContext(null);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [cart, setCart] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Memoize loadCart to prevent infinite loops
  const loadCart = useCallback(async () => {
    if (!isAuthenticated) return;

    setLoading(true);
    setError(null);
    try {
      const cartData = await cartService.getCart();
      setCart(cartData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  // Load cart when user is authenticated
  useEffect(() => {
    if (isAuthenticated) {
      loadCart();
    } else {
      setCart(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const addToCart = async (productId, quantity = 1, colorId = null) => {
    if (!isAuthenticated) {
      throw new Error('Please login to add items to cart');
    }

    setLoading(true);
    setError(null);
    try {
      await cartService.addToCart(productId, quantity, colorId);
      // Backend returns { cartItem }, so reload the full cart
      await loadCart();
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateCartItem = async (itemId, quantity) => {
    if (!isAuthenticated) {
      throw new Error('Please login to update cart');
    }

    setLoading(true);
    setError(null);
    try {
      await cartService.updateCartItem(itemId, quantity);
      // Backend returns { cartItem }, so reload the full cart
      await loadCart();
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const removeFromCart = async itemId => {
    if (!isAuthenticated) {
      throw new Error('Please login to remove items from cart');
    }

    setLoading(true);
    setError(null);
    try {
      await cartService.removeFromCart(itemId);
      // Backend returns success message, so reload the full cart
      await loadCart();
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const clearCart = async () => {
    if (!isAuthenticated) {
      throw new Error('Please login to clear cart');
    }

    setLoading(true);
    setError(null);
    try {
      await cartService.clearCart();
      setCart(null);
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Calculate cart totals
  const cartTotal =
    cart?.items?.reduce((total, item) => {
      // Ensure price is a number (might come as string from backend)
      const price = parseFloat(item.product?.price || 0);
      return total + price * item.quantity;
    }, 0) || 0;

  const cartItemCount =
    cart?.items?.reduce((count, item) => {
      return count + item.quantity;
    }, 0) || 0;

  const value = {
    cart,
    loading,
    error,
    cartTotal,
    cartItemCount,
    loadCart,
    addToCart,
    updateCartItem,
    removeFromCart,
    clearCart,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
